import { Injectable } from '@angular/core';

/**
 * Service for extracting data from Google Geocoding API responses
 *
 * Provides helper methods to parse and extract useful information
 * from geocoding results, such as city names.
 *
 * @example
 * constructor(private geocodingHelper: GeocodingHelperService) {}
 *
 * const city = this.geocodingHelper.extractCity(geocodingResults);
 */
@Injectable()
export class GeocodingHelperService {

  constructor() { }

  /**
   * Extracts the locality (city) name from geocoding results
   *
   * @param geocodingResults - Results from Google Geocoding API
   * @returns City name or empty string if not found
   *
   * @example
   * const results = { results: [{ address_components: [...] }] };
   * const city = extractCity(results);
   * // Returns: "Mexico City" or ""
   */
  extractCity(geocodingResults: any): string {
    if (!geocodingResults || !geocodingResults.results) {
      return '';
    }

    let cityName = '';

    // Iterate through results to find locality
    geocodingResults.results.forEach((result: any) => {
      if (!result.address_components) {
        return;
      }

      result.address_components.forEach((component: any) => {
        if (!component.types) {
          return;
        }

        component.types.forEach((type: string) => {
          if (type === 'locality') {
            cityName = component.long_name;
          }
        });
      });
    });

    return cityName;
  }

  /**
   * Extracts cities from both origin and destination geocoding results
   *
   * @param origenResults - Geocoding results for origin
   * @param destinoResults - Geocoding results for destination
   * @returns Object with cityO (origin) and cityD (destination)
   *
   * @example
   * const cities = extractCities(origenGeocode, destinoGeocode);
   * // Returns: { cityO: "Guadalajara", cityD: "Mexico City" }
   */
  extractCities(origenResults: any, destinoResults: any): {
    cityO: string,
    cityD: string
  } {
    return {
      cityO: this.extractCity(origenResults),
      cityD: this.extractCity(destinoResults)
    };
  }

  /**
   * Extracts a specific address component type from geocoding results
   *
   * @param geocodingResults - Results from Google Geocoding API
   * @param componentType - Type of component to extract (e.g., 'locality', 'country', 'postal_code')
   * @param nameType - 'long_name' or 'short_name' (default: 'long_name')
   * @returns Component value or empty string if not found
   *
   * @example
   * extractComponent(results, 'country', 'short_name')
   * // Returns: "MX"
   */
  extractComponent(
    geocodingResults: any,
    componentType: string,
    nameType: 'long_name' | 'short_name' = 'long_name'
  ): string {
    if (!geocodingResults || !geocodingResults.results) {
      return '';
    }

    let componentValue = '';

    geocodingResults.results.forEach((result: any) => {
      if (!result.address_components) {
        return;
      }

      result.address_components.forEach((component: any) => {
        if (!component.types) {
          return;
        }

        if (component.types.includes(componentType)) {
          componentValue = component[nameType];
        }
      });
    });

    return componentValue;
  }

  /**
   * Extracts full formatted address from geocoding results
   *
   * @param geocodingResults - Results from Google Geocoding API
   * @returns Formatted address or empty string
   */
  extractFormattedAddress(geocodingResults: any): string {
    if (!geocodingResults || !geocodingResults.results || geocodingResults.results.length === 0) {
      return '';
    }

    return geocodingResults.results[0].formatted_address || '';
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/geocoding-helper.service.ts